import { forwardRef, ReactNode, useCallback, useImperativeHandle, useRef, useState } from 'react';
import type { DropEvent, DropzoneOptions, FileRejection, FileWithPath } from 'react-dropzone';

import { Dropzone, DropzoneProps } from '../../../primitives/dropzone';
import { cn } from '../../../utils/cn';
import { Preview, type PreviewRef } from './components/preview';
import { type FileData as DropzoneMediaFileData } from './utils/get-files-data';

/**
 * TODO
 * - show not accepted files because its format
 * - show files that were not accepted because they exceeded the max file limit
 */

export { DropzoneMediaFileData };

export type DropzoneMediaRef = {
  getAcceptedFiles: () => { acceptedFiles: DropzoneMediaFileData[] };
  getRejectedFiles: () => { rejectedFiles: DropzoneMediaFileData[] };
  reset: (except?: DropzoneMediaFileData[]) => void;
};

const DEFAULT_SIZE_ALERT = {
  none: 5_000_000,
  medium: 10_000_000,
};

interface DropzoneMediaProps extends DropzoneProps {
  disabled?: boolean;
  popoverWarningDetail?: ReactNode;
  sizeAlert?: {
    none: number;
    medium: number;
  };
}

/**
 * @param className **optional** className for wrapper
 * @param options **optional** an object with all the callbacks and dropzone options to handle file upload and more. See: https://react-dropzone.js.org/#section-components
 * for `options/accept` see https://www.iana.org/assignments/media-types/media-types.xhtml#image
 * @param sizeAlert **optional** helps to point big files
 * @param description **optional** dropzone text
 * @param icon **optional** dropzone icon
 * @param label **optional** dropzone main text
 * @param popoverWarningDetail **optional** additional info to show in a popover when the file size exceeds the size alert value.
 *
 * @example
 * <>
 *  <div className="space-y-2">
 *    <p className="font-semibold">Posibles consecuencias</p>
 *    <ul className="space-y-2">
 *      <li>
 *        <p>Rendimiento reducido</p>
 *        <p className="text-muted-foreground">Carga lenta de la página</p>
 *        <p className="text-muted-foreground">Mayor tasa de abandono</p>
 *      </li>
 *
 *      <li>
 *        <p>Experiencia degradada</p>
 *        <p className="text-muted-foreground">Interrupciones o errores de visualización</p>
 *      </li>
 *    </ul>
 *  </div>
 *  <div className="space-y-2">
 *    <p className="font-semibold">Recomendación</p>
 *    <p className="text-muted-foreground">
 *      <span className="text-primary">Optimiza los archivos antes de subirlos</span> para
 *      garantizar un rendimiento óptimo
 *    </p>
 *  </div>
 * </>
 *
 * @function `getAcceptedFiles()` **optional** accesible by ref. Return formatted data. Note: onDrop callbacks returns raw data
 * @function `getRejectedFiles()` **optional** accesible by ref. Return formatted data. Note: onDrop callbacks returns raw data
 * @function `reset()` **optional** accesible by ref
 *
 * @example
 * const dropzoneMediaRef = useRef<DropzoneMediaRef>(null);
 *
 * const handleSubmit = () => {
 *  const getAcceptedFiles = dropzoneMediaRef?.current?.getAcceptedFiles;
 *  if (!getAcceptedFiles) return;
 *  const { acceptedFiles } = getAcceptedFiles();
 *  console.info('acceptedFiles:', acceptedFiles);
 * }
 *
 * <DropzoneMedia
 *  description="JPEG | PNG | GIF | MP4 | MOV"
 *  disabled={isLoading}
 *  options={{
 *    accept: {
 *      'image/jpeg': [],
 *      'image/png': [],
 *    },
 *  }}
 *  ref={dropzoneMediaRef}
 * />
 */
export const DropzoneMedia = forwardRef<DropzoneMediaRef, DropzoneMediaProps>(
  function DropzoneMedia(
    {
      className,
      disabled = false,
      options,
      popoverWarningDetail,
      sizeAlert = DEFAULT_SIZE_ALERT,
      ...dropzoneMediaInputProps
    },
    ref,
  ) {
    // executes the custom `onDrop` callback from parent and preserves the one defined in this component
    let restOptions: DropzoneOptions | undefined = options;
    let parentOnDrop: any = () => {};
    if (options?.onDrop) {
      const { onDrop, ...rest } = options;
      parentOnDrop = onDrop;
      restOptions = rest;
    }

    const previewRef = useRef<PreviewRef>(null);

    const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
    const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);

    const handleOnDrop = useCallback(
      (acceptedFiles: FileWithPath[], rejectedFiles: FileRejection[], event: DropEvent) => {
        setAcceptedFiles(acceptedFiles);
        setRejectedFiles(rejectedFiles);
        parentOnDrop(acceptedFiles, rejectedFiles, event);
      },
      [],
    );

    useImperativeHandle(ref, () => ({
      getAcceptedFiles() {
        const getAcceptedFiles = previewRef?.current?.getAcceptedFiles;
        if (getAcceptedFiles) {
          const { acceptedFiles } = getAcceptedFiles();
          return { acceptedFiles };
        }
        return { acceptedFiles: [] };
      },
      getRejectedFiles() {
        const getRejectedFiles = previewRef?.current?.getRejectedFiles;
        if (getRejectedFiles) {
          const { rejectedFiles } = getRejectedFiles();
          return { rejectedFiles };
        }
        return { rejectedFiles: [] };
      },
      reset(except: DropzoneMediaFileData[] = []) {
        const filteredAcceptedFiles: File[] = [];
        const filteredRejectedFiles: FileRejection[] = [];
        except.forEach((exceptFile) => {
          const findAcceptedFile = acceptedFiles.find(
            (item) => item.name.replace(/\s/g, '_') === exceptFile.name,
          );
          const findRejectedFile = rejectedFiles.find(
            (item) => item.file.name.replace(/\s/g, '_') === exceptFile.name,
          );
          if (findAcceptedFile) {
            filteredAcceptedFiles.push(findAcceptedFile);
          }
          if (findRejectedFile) {
            filteredRejectedFiles.push(findRejectedFile);
          }
        });
        setAcceptedFiles(filteredAcceptedFiles);
        setRejectedFiles(filteredRejectedFiles);
      },
    }));

    return (
      <div className={cn('grid gap-4', className)}>
        <Dropzone
          {...dropzoneMediaInputProps}
          options={{ disabled: disabled, onDrop: handleOnDrop, ...restOptions }}
        />
        <Preview
          acceptedFiles={acceptedFiles}
          disabled={disabled}
          popoverWarningDetail={popoverWarningDetail}
          ref={previewRef}
          rejectedFiles={rejectedFiles}
          sizeAlert={sizeAlert}
        />
      </div>
    );
  },
);
